import React from 'react';
import type { WeekFlag, WbsFlatItem } from './types';
import StatusBadge from './components/common/StatusBadge';
import WbsProgressChip from './components/common/WbsProgressChip';
import { statusFromRow } from './components/common/statusFromRow';
import {
    getCompletedCarryForWeek,
    getComparisonValue,
    getWeekMinusOneColumnLabel,
} from './lib/weeklyEvDisplay';

type Props = {
    projectId: string;
    canEdit: boolean;
    visibleRows: WbsFlatItem[];
    flags: Record<string, WeekFlag>;
    weeklyProgressComparison?: any | null;
    approvedMapW1?: Record<string, { approvedValue: number; sourceWeekOf: string }>;
    approvedMapW2?: Record<string, { approvedValue: number; sourceWeekOf: string }>;
    displayParentIds: Set<string>;
    rollupParentIds: Set<string>;
    editableRowIds?: Set<string>;
    pvRefDate: Date;
    refFriday: Date;
    selectedIds: Set<string>;
    setSelectedIds: React.Dispatch<React.SetStateAction<Set<string>>>;
    evInput: Record<string, string>;
    setEvInput: React.Dispatch<React.SetStateAction<Record<string, string>>>;
    dirtyIds: Set<string>;
    setDirtyIds: React.Dispatch<React.SetStateAction<Set<string>>>;
    saveSelected: () => Promise<void> | void;
    saving: boolean;
    error: string | null;
    setError: React.Dispatch<React.SetStateAction<string | null>>;
    entryClosed?: boolean;
    entryAutoApproveAt?: Date;
    lastRefreshedAt?: Date | null;
    refreshing?: boolean;
    onRefresh?: () => Promise<void> | void;
    onOpenHistory: (row: WbsFlatItem) => void;
    onOpenNote: (note: string) => void;
};

function formatPct(n: number | null | undefined) {
    return n == null || !Number.isFinite(n) ? '-' : `${Number(n).toFixed(1)}%`;
}

export function WbsProgressEntryMobileList({
    canEdit,
    visibleRows,
    flags,
    weeklyProgressComparison,
    approvedMapW1,
    approvedMapW2,
    displayParentIds,
    rollupParentIds,
    editableRowIds,
    pvRefDate,
    refFriday,
    selectedIds,
    setSelectedIds,
    evInput,
    setEvInput,
    dirtyIds,
    setDirtyIds,
    saveSelected,
    saving,
    error,
    setError,
    entryClosed,
    entryAutoApproveAt,
    lastRefreshedAt,
    refreshing = false,
    onRefresh,
    onOpenHistory,
    onOpenNote,
}: Props) {
    const toggleSelect = (id: string, checked: boolean) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (checked) next.add(id);
            else next.delete(id);
            return next;
        });
    };

    return (
        <section className="overflow-hidden rounded-2xl border bg-white">
            <div className="border-b px-4 py-3 space-y-2">
                <div className="flex items-start justify-between gap-3">
                    <div>
                        <div className="text-base font-semibold">Weekly Progress Entry</div>
                        <div className="text-xs text-gray-500">Mobile Input</div>
                    </div>
                    {lastRefreshedAt && !refreshing ? (
                        <div className="text-[11px] text-gray-500">
                            {lastRefreshedAt.toLocaleTimeString('ko-KR')} 갱신
                        </div>
                    ) : null}
                </div>
            </div>

            {error && (
                <div className="mx-4 mt-3 rounded-md border border-rose-300 bg-rose-50 px-3 py-2 text-sm text-rose-800">
                    <div className="flex items-start justify-between gap-3">
                        <div>{error}</div>
                        <button
                            type="button"
                            className="shrink-0 rounded px-2 py-1 text-xs hover:bg-red-100"
                            onClick={() => setError(null)}
                        >
                            X
                        </button>
                    </div>
                </div>
            )}

            <div className="space-y-3 p-3 pb-24">
                {visibleRows.map((r) => {
                    const f = flags?.[r.id];
                    const plannedStarted = r.plannedStart;
                    const kind = statusFromRow(r, r.plannedValue ?? null, pvRefDate);
                    const completedCarryW2 = getCompletedCarryForWeek(r, new Date(refFriday.getTime() - 14 * 86400000));
                    const confirmedEV =
                        approvedMapW2?.[r.nodeId]?.approvedValue ??
                        getComparisonValue(weeklyProgressComparison?.n2?.approved?.[r.nodeId]) ??
                        completedCarryW2;
                    const completedCarryW1 = getCompletedCarryForWeek(r, new Date(refFriday.getTime() - 7 * 86400000));
                    const week1Approved = getComparisonValue(weeklyProgressComparison?.n1?.approved?.[r.nodeId]);
                    const week1Provisional = getComparisonValue(weeklyProgressComparison?.n1?.provisional?.[r.nodeId]);
                    const week1Label = getWeekMinusOneColumnLabel(weeklyProgressComparison);
                    const week1Value =
                        week1Provisional ??
                        approvedMapW1?.[r.nodeId]?.approvedValue ??
                        week1Approved ??
                        completedCarryW1;
                    const previousWeekApproved = approvedMapW1?.[r.nodeId]?.approvedValue ?? r.earnedValue ?? null;
                    const previousWeekEffective =
                        f?.previousWeekStatus === 'PENDING' || f?.previousWeekStatus === 'APPROVED'
                            ? (f.previousWeekDraftEV ?? previousWeekApproved)
                            : previousWeekApproved;
                    const provisionalEV = r.liveEarnedValue ?? f?.liveEV ?? f?.draftEV ?? previousWeekEffective;
                    const rejectionNote = (f?.rejectionNote ?? '').trim() || null;
                    const checked = selectedIds.has(r.id);
                    const value = evInput[r.id] ?? '';
                    const hasChildrenUi = displayParentIds.has(r.id);
                    const hasChildrenRollup = rollupParentIds.has(r.id);
                    const isParentWbs = hasChildrenUi || hasChildrenRollup;
                    const isEditableRow = editableRowIds ? editableRowIds.has(r.id) : true;
                    const isApprovedLocked = f?.statusForWeek === 'APPROVED';
                    const pendingPreviousWeek = f?.previousWeekStatus === 'PENDING';
                    const isCompleted = (confirmedEV ?? 0) >= 100;
                    const resubmitAllowed =
                        !entryClosed ||
                        (f?.statusForWeek === 'REJECTED' &&
                            !!entryAutoApproveAt &&
                            new Date() < new Date(entryAutoApproveAt));
                    const canEditRow =
                        canEdit &&
                        isEditableRow &&
                        !hasChildrenRollup &&
                        !isCompleted &&
                        !isApprovedLocked &&
                        (!entryClosed || resubmitAllowed);

                    if (isParentWbs) {
                        return (
                            <article key={r.id} className="rounded-xl border px-3 py-2.5 shadow-sm">
                                <div className="flex items-start gap-3">
                                    <div className="min-w-0 flex-1">
                                        <div className="text-[11px] text-gray-500">{r.code}</div>
                                        <button
                                            type="button"
                                            title={r.name}
                                            className="mt-0.5 block w-full truncate text-left text-sm font-medium text-gray-900 //hover:underline"
                                            onClick={() => onOpenHistory(r)}
                                        >
                                            {r.name}
                                        </button>
                                        <div className="mt-1 text-[11px] text-gray-500">
                                            {r.plannedStart ?? '-'} ~ {r.plannedFinish ?? '-'}
                                        </div>
                                    </div>
                                </div>
                            </article>
                        );
                    }

                    return (
                        <article key={r.id} className="rounded-2xl border p-3 shadow-sm">
                            <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0 flex-1">
                                    <div className="text-[11px] text-gray-500">{r.code}</div>
                                    <button
                                        type="button"
                                        className="mt-1 block w-full truncate text-left font-medium text-gray-900"
                                        onClick={() => onOpenHistory(r)}
                                    >
                                        {hasChildrenUi ? '▸ ' : ''}
                                        {r.name}
                                    </button>
                                    <div className="mt-1 text-[11px] text-gray-500">
                                        {r.plannedStart ?? '-'} ~ {r.plannedFinish ?? '-'}
                                    </div>
                                </div>
                                <div className="flex flex-col items-end gap-1">
                                    <StatusBadge kind={kind} />
                                    {(plannedStarted || !!f?.hasSubmissionForWeek) && (
                                        <WbsProgressChip status={f?.statusForWeek ?? null} />
                                    )}
                                    {pendingPreviousWeek && (
                                        <div className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] text-amber-800">
                                            전주 승인중
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                                <div className="rounded-lg bg-gray-50 px-2 py-2">
                                    <div className="text-gray-500">수행사</div>
                                    <div className="mt-1 truncate">{r.executer ?? '-'}</div>
                                </div>
                                <div className="rounded-lg bg-gray-50 px-2 py-2">
                                    <div className="text-gray-500">계획</div>
                                    <div className="mt-1 font-medium">{formatPct(r.plannedValue ?? null)}</div>
                                </div>
                                <div className="rounded-lg bg-gray-50 px-2 py-2">
                                    <div className="text-gray-500">확정(W-2주)</div>
                                    <div className="mt-1 font-medium">{formatPct(confirmedEV)}</div>
                                </div>
                                <div className="rounded-lg bg-amber-50 px-2 py-2">
                                    <div className="text-gray-500">{week1Label}</div>
                                    <div className="mt-1 font-medium">{formatPct(week1Value)}</div>
                                </div>
                                <div className="rounded-lg bg-amber-50 px-2 py-2">
                                    <div className="text-gray-500">잠정(금주)</div>
                                    <div className="mt-1 font-medium">{formatPct(provisionalEV)}</div>
                                </div>
                            </div>

                            <div className="mt-3">
                                {canEditRow && (
                                    <label className="mb-1 flex items-center gap-2 text-xs font-medium text-gray-600">
                                        <input
                                            type="checkbox"
                                            checked={checked}
                                            onChange={(e) => toggleSelect(r.id, e.target.checked)}
                                            disabled={!canEditRow}
                                        />
                                        금주 입력
                                    </label>
                                )}
                                <div className="relative">
                                    <input
                                        type="number"
                                        min={0}
                                        max={100}
                                        step={0.1}
                                        inputMode="decimal"
                                        value={value}
                                        disabled={!canEditRow}
                                        onChange={(e) => {
                                            const v = e.target.value;
                                            setEvInput((prev) => ({ ...prev, [r.id]: v }));
                                            setDirtyIds((prev) => {
                                                const next = new Set(prev);
                                                next.add(r.id);
                                                return next;
                                            });
                                            setSelectedIds((prev) => {
                                                const next = new Set(prev);
                                                next.add(r.id);
                                                return next;
                                            });
                                        }}
                                        className="w-full rounded-xl border px-3 py-3 pr-8 text-right text-base disabled:bg-gray-50 disabled:text-gray-400"
                                    />
                                    <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-400">
                                        %
                                    </span>
                                </div>
                                {dirtyIds.has(r.id) && (
                                    <div className="mt-1 text-[11px] text-amber-600">변경됨 (미저장)</div>
                                )}
                                {pendingPreviousWeek && (
                                    <div className="mt-1 text-[11px] text-amber-700">전주 실적 일부가 아직 승인중입니다.</div>
                                )}
                            </div>

                            {rejectionNote && (
                                <div className="mt-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
                                    반려: {rejectionNote}
                                    <button
                                        type="button"
                                        className="ml-2 underline"
                                        onClick={() => onOpenNote(rejectionNote)}
                                    >
                                        자세히
                                    </button>
                                </div>
                            )}
                        </article>
                    );
                })}

                {visibleRows.length === 0 && (
                    <div className="rounded-xl border px-3 py-6 text-center text-sm text-gray-500">
                        항목이 없습니다.
                    </div>
                )}
            </div>

            <div className="sticky bottom-0 border-t bg-white/95 px-3 py-3 backdrop-blur">
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        onClick={onRefresh}
                        disabled={refreshing}
                        className="flex-1 rounded-xl border px-3 py-3 text-sm disabled:opacity-50"
                    >
                        {refreshing ? 'Refreshing...' : 'Refresh'}
                    </button>
                    <button
                        type="button"
                        onClick={saveSelected}
                        disabled={entryClosed || saving || selectedIds.size === 0}
                        className="flex-1 rounded-xl bg-gray-900 px-3 py-3 text-sm text-white disabled:opacity-50"
                    >
                        {saving ? 'Submitting...' : 'Submit'}
                    </button>
                </div>
            </div>
        </section>
    );
}
